import { createContext, ReactNode, useContext } from 'react';
import { BoxProps, createStylesContext, forwardRef, SpaceProps, useMultiStyleConfig } from '@chakra-ui/react';
import Box from '../Box/Box';
import Text from '../Text/Text';
import Divider from '../Divider/Divider';
import Icon from '../Icon/Icon';

const [SidebarContext, useSidebarStyle] = createStylesContext('Sidebar');

const SidebarContainer = ({ children }: { children: ReactNode }) => {
  const { container } = useSidebarStyle();
  return <Box sx={container}>{children}</Box>;
};

const SidebarHeader = ({ title }: { title?: string }) => {
  if (!title) {
    return <Box h="24" w="100%" />;
  }
  return (
    <Text fontWeight="bold" px="32" py="1.375rem" size={['6', '4']}>
      {title}
    </Text>
  );
};

const FooterContext = createContext(false);

const SidebarDivider = () => {
  const isInFooter = useContext(FooterContext);
  const margins: SpaceProps = isInFooter ? { marginTop: '12' } : { mx: '32', my: '24' };
  return <Divider {...margins} w="auto" />;
};

const SidebarGroupHeader = ({ label }: { label: string }) => {
  return (
    <Box alignItems="center" display="flex" gap="16" marginBottom="12" marginTop="24" mx="32">
      <Text as="h6" textStyle="heading/h6" textColor="neutral.60">
        {label}
      </Text>
      <Divider flexGrow="1" w="auto" />
    </Box>
  );
};

const SidebarFooter = ({ children }: { children: ReactNode }) => {
  const { container } = useSidebarStyle();
  return (
    <FooterContext.Provider value>
      <Box marginTop="auto" sx={container}>
        {children}
      </Box>
    </FooterContext.Provider>
  );
};

export type SidebarProps = { children: ReactNode; title?: string } & Omit<
  BoxProps,
  'display' | 'd' | 'flexDirection' | 'flexDir' | 'as'
>;
const Sidebar = forwardRef<SidebarProps, 'nav'>(({ children, title, ...boxProps }, ref) => {
  const theme = useMultiStyleConfig('Sidebar');
  return (
    <SidebarContext value={theme}>
      <Box as="nav" display="flex" flexDirection="column" {...boxProps} ref={ref}>
        {title && <SidebarHeader title={title} />}
        {children}
      </Box>
    </SidebarContext>
  );
});

const SidebarButton = ({ onClick, title }: Pick<BoxProps, 'onClick'> & { title: string }) => {
  return (
    <Box alignItems="center" as="button" type="button" display="flex" gap="4" h="56" onClick={onClick} paddingLeft="16">
      <Icon name="ChevronLeft" size="24" />
      <Text as="span" lineHeight="var(--space-24)">
        {title}
      </Text>
    </Box>
  );
};

export default Sidebar;
export { SidebarDivider, SidebarGroupHeader, SidebarContainer, SidebarFooter, SidebarButton };
